import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import SyncIcon from '@mui/icons-material/Sync';
import { useSnackbar } from 'notistack';

import { CREATE, DataAPIContext } from '../api';
import UploadDocumentButton from '../components/UploadDocumentButton';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { BackgroundTaskContext } from '../bg_tasks/BackgroundTaskProvider';
import {
    isBackgroundTaskResponse,
    notifyBackgroundTask,
} from '../bg_tasks/batchResponseHelper';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FormatName = 'format1' | 'format2' | 'format3';

interface FieldDescriptor {
    key: string;
    label: string;
    required: boolean;
}

interface FormatDescriptor {
    label: string;
    fields: FieldDescriptor[];
}

interface PreviewResponse {
    cache_key: string;
    columns: string[];
    preview: string[][];
    row_count: number;
    source: { kind: 'file' | 'url'; name: string };
    autodetect: { format: FormatName | null; mapping: Record<string, string | null> };
    formats: Record<FormatName, FormatDescriptor>;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(() => ({
    root: { padding: 16, maxWidth: 1100 },
    paper: { padding: 24, marginBottom: 16 },
    formatSummary: { marginBottom: 16 },
    previewTable: { maxHeight: 360 },
    mappedHeader: { backgroundColor: '#e8f5e9' },
    actions: { marginTop: 16, display: 'flex', gap: 12 },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OtaImportWizard = () => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const bgCtx = React.useContext(BackgroundTaskContext);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    // Step state
    const [activeStep, setActiveStep] = React.useState(0);

    // Step 1 inputs
    const [inputTab, setInputTab] = React.useState<'file' | 'url'>('file');
    const [file, setFile] = React.useState<File | null>(null);
    const [sheetUrl, setSheetUrl] = React.useState('');
    const [previewing, setPreviewing] = React.useState(false);

    // Step 2 state (from preview response)
    const [preview, setPreview] = React.useState<PreviewResponse | null>(null);
    const [format, setFormat] = React.useState<FormatName | ''>('');
    const [mapping, setMapping] = React.useState<Record<string, string>>({});

    // Step 2 submit state
    const [submitting, setSubmitting] = React.useState(false);

    const onFileSelect = (selected: File | null) => setFile(selected || null);
    const onFileClear = () => setFile(null);

    const canPreview =
        (inputTab === 'file' && !!file) ||
        (inputTab === 'url' &&
            /^https:\/\/docs\.google\.com\/spreadsheets\/d\/(e\/)?[A-Za-z0-9_-]+/.test(sheetUrl.trim()));

    const handlePreview = async () => {
        setPreviewing(true);
        try {
            let res;
            if (inputTab === 'file' && file) {
                res = await dataAPI(CREATE, 'ota_import', {
                    urlSuffix: 'preview/',
                    files: { file },
                    data: {},
                });
            } else {
                res = await dataAPI(CREATE, 'ota_import', {
                    urlSuffix: 'preview/',
                    data: { sheet_url: sheetUrl.trim() },
                });
            }

            if (res?.statusCode >= 400) {
                enqueueSnackbar(res.data?.message || 'Preview failed', { variant: 'error' });
                return;
            }

            const data = res.data as PreviewResponse;
            setPreview(data);
            const fmt = data.autodetect.format ?? '';
            setFormat(fmt);
            setMapping(
                Object.fromEntries(
                    Object.entries(data.autodetect.mapping || {}).filter(([, v]) => !!v) as [string, string][],
                ),
            );
            setActiveStep(1);
        } catch (e: any) {
            enqueueSnackbar(e?.message || 'Preview failed', { variant: 'error' });
        } finally {
            setPreviewing(false);
        }
    };

    const formatDescriptor: FormatDescriptor | null =
        preview && format ? preview.formats[format] : null;

    const mappedColumns = new Set(Object.values(mapping).filter(Boolean));

    const requiredMissing = formatDescriptor
        ? formatDescriptor.fields.filter((f) => f.required && !mapping[f.key]).map((f) => f.label)
        : [];

    const handleMappingChange = (logicalKey: string, columnName: string) => {
        setMapping((prev) => ({ ...prev, [logicalKey]: columnName }));
    };

    const submitImport = async (dryRun: boolean) => {
        if (!preview || !format) return;
        setSubmitting(true);
        try {
            const res = await dataAPI(CREATE, 'ota_import', {
                urlSuffix: 'start/',
                data: {
                    cache_key: preview.cache_key,
                    format,
                    mapping,
                    dry_run: dryRun,
                },
            });

            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                handleReset();
                return;
            }
            if (res?.statusCode >= 400) {
                enqueueSnackbar(res.data?.message || 'Failed to start import', { variant: 'error' });
                return;
            }
            enqueueSnackbar('Import started', { variant: 'success' });
            handleReset();
        } catch (e: any) {
            enqueueSnackbar(e?.message || 'Failed to start import', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setFile(null);
        setSheetUrl('');
        setPreview(null);
        setFormat('');
        setMapping({});
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <div className={classes.root}>
            <PageMeta title="Import OTAs" />
            <PageHeading sx={{ mb: 1 }}>Import OTAs</PageHeading>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Bulk-create Online Travel Agency entries and their email/domain rules
                from a CSV/Excel file or a public Google Sheet.
            </Typography>

            <Paper className={classes.paper}>
                <Stepper activeStep={activeStep} orientation="vertical">
                    <Step>
                        <StepLabel>Provide the spreadsheet</StepLabel>
                        <StepContent>
                            <Tabs
                                value={inputTab}
                                onChange={(_, v) => setInputTab(v)}
                                sx={{ mb: 2 }}
                            >
                                <Tab label="Upload file" value="file" />
                                <Tab label="Google Sheets URL" value="url" />
                            </Tabs>

                            {inputTab === 'file' && (
                                <Box sx={{ mb: 2 }}>
                                    <UploadDocumentButton
                                        file={file}
                                        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                        onSelect={onFileSelect}
                                        onClearFile={onFileClear}
                                        customLabel="Choose CSV or XLSX"
                                    />
                                </Box>
                            )}

                            {inputTab === 'url' && (
                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Google Sheets URL"
                                        helperText="'Anyone with the link' (Viewer), or for Workspace sheets use File → Share → Publish to web (CSV)."
                                        value={sheetUrl}
                                        onChange={(e) => setSheetUrl(e.target.value)}
                                        placeholder="https://docs.google.com/spreadsheets/d/..."
                                    />
                                </Box>
                            )}

                            <div className={classes.actions}>
                                <Button
                                    variant="contained"
                                    onClick={handlePreview}
                                    disabled={!canPreview || previewing}
                                >
                                    {previewing ? <CircularProgress size={20} /> : 'Next: preview and map'}
                                </Button>
                            </div>
                        </StepContent>
                    </Step>

                    <Step>
                        <StepLabel>Choose columns and import</StepLabel>
                        <StepContent>
                            {preview && (
                                <>
                                    <Alert severity="info" className={classes.formatSummary}>
                                        Source: <strong>{preview.source.name}</strong> &mdash;{' '}
                                        {preview.row_count} row{preview.row_count === 1 ? '' : 's'}.
                                    </Alert>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>CSV format</InputLabel>
                                                <Select
                                                    label="CSV format"
                                                    value={format}
                                                    onChange={(e) => {
                                                        setFormat(e.target.value as FormatName);
                                                        setMapping({});
                                                    }}
                                                >
                                                    {Object.entries(preview.formats).map(([key, descriptor]) => (
                                                        <MenuItem key={key} value={key}>
                                                            {descriptor.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    {formatDescriptor && (
                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            {formatDescriptor.fields.map((field) => (
                                                <Grid item xs={12} md={6} key={field.key}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {field.label}
                                                            {field.required ? ' *' : ''}
                                                        </InputLabel>
                                                        <Select
                                                            label={field.label}
                                                            value={mapping[field.key] || ''}
                                                            onChange={(e) =>
                                                                handleMappingChange(field.key, e.target.value as string)
                                                            }
                                                        >
                                                            <MenuItem value="">
                                                                <em>{field.required ? '— select —' : 'Not mapped'}</em>
                                                            </MenuItem>
                                                            {preview.columns.map((col) => (
                                                                <MenuItem key={col} value={col}>
                                                                    {col}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}

                                    {requiredMissing.length > 0 && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            Map the following required field(s): {requiredMissing.join(', ')}.
                                        </Alert>
                                    )}

                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Preview (first {preview.preview.length} rows)
                                    </Typography>
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        className={classes.previewTable}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {preview.columns.map((col) => (
                                                        <TableCell
                                                            key={col}
                                                            className={
                                                                mappedColumns.has(col) ? classes.mappedHeader : ''
                                                            }
                                                        >
                                                            {col}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {preview.preview.map((row, ri) => (
                                                    <TableRow key={ri}>
                                                        {preview.columns.map((col, ci) => (
                                                            <TableCell
                                                                key={col}
                                                                className={
                                                                    mappedColumns.has(col) ? classes.mappedHeader : ''
                                                                }
                                                            >
                                                                {row[ci]}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* CP-8423: [BACK] [DRY RUN] [START IMPORT], bottom-right. */}
                                    <div className={classes.actions} style={{ justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ArrowBackIcon />}
                                            onClick={() => setActiveStep(0)}
                                            disabled={submitting}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={submitting ? undefined : <CallSplitIcon />}
                                            onClick={() => submitImport(true)}
                                            disabled={submitting || requiredMissing.length > 0 || !format}
                                        >
                                            {submitting ? <CircularProgress size={20} /> : 'Dry run'}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={submitting ? undefined : <SyncIcon />}
                                            onClick={() => submitImport(false)}
                                            disabled={submitting || requiredMissing.length > 0 || !format}
                                        >
                                            {submitting ? <CircularProgress size={20} /> : 'Start import'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </StepContent>
                    </Step>
                </Stepper>
            </Paper>
        </div>
    );
};

export default OtaImportWizard;
